import { useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

/**
 * Local development overrides for LaunchDarkly flags.
 *
 * Set REACT_APP_FLAG_OVERRIDES in `.env.local` (gitignored) to force flags on or off without
 * touching LaunchDarkly, which is useful for a flag that is not yet targeted at your user:
 *
 *   REACT_APP_FLAG_OVERRIDES={"leslie":true}
 *
 * Parsed once at module load, and only while Vite is running in development. A production
 * bundle evaluates `import.meta.env.DEV` to false, so LaunchDarkly stays the single source of
 * truth there even if the variable somehow survives into the build environment.
 */
const parseOverrides = () => {
  if (!import.meta.env.DEV) return {};

  const raw = import.meta.env.REACT_APP_FLAG_OVERRIDES;
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? parsed
      : {};
  } catch (error) {
    // A malformed override must not take the app down — fall back to the real flags.
    return {};
  }
};

const overrides = parseOverrides();
const hasOverrides = Object.keys(overrides).length > 0;

/**
 * LaunchDarkly flags with any local development overrides applied on top.
 *
 * The object LaunchDarkly returns is passed through untouched whenever there is nothing to
 * merge — always in a production bundle, and in development until someone actually sets
 * REACT_APP_FLAG_OVERRIDES. Spreading unconditionally would hand back a new object every
 * render, so any consumer holding the flags object in a dependency array would re-run on
 * every render. When overrides are active the merged object is memoised on the same input
 * for the same reason.
 */
export function useAppFlags() {
  const flags = useFlags();

  return useMemo(
    () =>
      import.meta.env.DEV && hasOverrides ? { ...flags, ...overrides } : flags,
    [flags],
  );
}

export default useAppFlags;
